import { TemplateTypes } from '../../../../../../../lib/models/TemplateTypes';
import { Product } from '../../FindersAndProductsPage/lib/Product';

/**
 * A catalogue product without an external ID never clears the structure copy: the catalogue list can
 * be incomplete, and losing a configured ID is worse than keeping a stale one.
 */
function getExternalIdsByProductId(tenantProducts) {
  const externalIdsByProductId = new Map();

  if (!Array.isArray(tenantProducts)) {
    return externalIdsByProductId;
  }

  for (const tenantProduct of tenantProducts) {
    const externalId = tenantProduct?.[Product.EXTERNAL_ID];
    const productId = tenantProduct?.id;

    if (productId !== undefined && productId !== null && typeof externalId === 'string' && externalId.trim()) {
      externalIdsByProductId.set(String(productId), externalId);
    }
  }

  return externalIdsByProductId;
}

/**
 * The catalogue id is numeric, but the copy stored in the structure passes through select options and
 * JSON round-trips, so a legacy card may hold it as a string. Comparing as strings avoids a silent
 * no-match that would look exactly like the bug being fixed.
 */
function getProductIdKey(product) {
  const productId = product?.productNameSection?.product?.value;

  return productId === undefined || productId === null ? null : String(productId);
}

function getProductsWithSyncedExternalIds(products, externalIdsByProductId) {
  let hasChanges = false;

  const syncedProducts = products.map(product => {
    const productIdKey = getProductIdKey(product);
    const catalogueExternalId = productIdKey === null ? undefined : externalIdsByProductId.get(productIdKey);

    if (catalogueExternalId === undefined || product?.[Product.EXTERNAL_ID] === catalogueExternalId) {
      return product;
    }

    hasChanges = true;

    return { ...product, [Product.EXTERNAL_ID]: catalogueExternalId };
  });

  return hasChanges ? syncedProducts : products;
}

/**
 * A product card in the questionnaire keeps its own copy of the catalogue fields, and `externalId`
 * used to be written only while picking the product (`setProductFields`). Every product placed on a
 * page before that field existed, and every ID edited in the product settings afterwards, therefore
 * never reached the finder structure, so prefill had nothing to match against and silently did
 * nothing. Re-syncing the copy from the catalogue removes the need to re-select each product by hand.
 *
 * Only product cards are synced, because prefill reads product statuses from PRODUCT elements only.
 * Returns the same structure reference when nothing has to change, so callers can use identity to
 * decide whether a state update is needed at all.
 */
export function syncProductExternalIds(questionnaireStructure, tenantProducts) {
  const externalIdsByProductId = getExternalIdsByProductId(tenantProducts);

  if (!Array.isArray(questionnaireStructure) || externalIdsByProductId.size === 0) {
    return questionnaireStructure;
  }

  let hasChanges = false;

  const syncedStructure = questionnaireStructure.map(element => {
    if (element?.templateType !== TemplateTypes.PRODUCT || !Array.isArray(element?.content?.products)) {
      return element;
    }

    const syncedProducts = getProductsWithSyncedExternalIds(element.content.products, externalIdsByProductId);

    if (syncedProducts === element.content.products) {
      return element;
    }

    hasChanges = true;

    return { ...element, content: { ...element.content, products: syncedProducts } };
  });

  return hasChanges ? syncedStructure : questionnaireStructure;
}
